import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styled from 'styled-components';

import { PageDocumentIcon } from '../../../UI/NgIcons';
import { SkeletonLoader } from '../../../UI';
import { useGetBlockQuery, useGetBlocksQuery, useGetLayoutsQuery } from '../../../../services/blocks.service';
import { Block } from '../../../../services';
import { BlockPreview } from '../Blocks/components/BlockPreview';
import { extractSectionsFromBlockHtml, parsePartialsFromHtml } from '../../../../helpers/micrositeEditor.helpers';

export interface TemplatePage {
  id?: number;
  name: string;
  displayName?: string;
}

interface AddPageDialogProps {
  open: boolean;
  templatePages: TemplatePage[];
  onSelect: (page: TemplatePage) => void;
  onClose: () => void;
}

const StyledDialogTitle = styled(DialogTitle)`
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  padding: 20px 24px !important;
  font-size: 18px !important;
  font-weight: 600 !important;
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 4px 2px 8px;
`;

const StyledPageCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.palette.white};
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const StyledCardPreview = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  background: ${({ theme }) => theme.palette.white};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  position: relative;
  overflow: hidden;

  svg {
    width: 36px;
    height: 36px;
  }
`;

const StyledThumbnailInner = styled.div<{ $scale: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 1440px;
  height: 1080px;
  transform: scale(${({ $scale }) => $scale});
  transform-origin: top left;
  pointer-events: none;
`;

const StyledCardLabel = styled.div`
  padding: 10px 14px;
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.button};
  border-top: 1px solid ${({ theme }) => theme.palette.cardBorder};
`;

interface PageThumbnailProps {
  pageId: number;
  allPages: TemplatePage[];
  allBlocks: Block[];
  allLayouts: Block[];
}

const PageThumbnail: FC<PageThumbnailProps> = ({ pageId, allPages, allBlocks, allLayouts }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);

  const { data: pageBlock, isLoading: isPageLoading } = useGetBlockQuery(pageId as any);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setScale(entry.contentRect.width / 1440);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const allBlocksForPartials = useMemo(() => [...allBlocks, ...allLayouts], [allBlocks, allLayouts]);

  const cssCode = useMemo(() => {
    if (!pageBlock) return '';
    const { layoutName } = parsePartialsFromHtml(pageBlock.html || '');
    const layout =
      allLayouts.find((candidate) => candidate.name === layoutName) ??
      allBlocks.find((candidate) => candidate.name === layoutName);
    return [layout?.blockStylesheet?.css ?? '', pageBlock.blockStylesheet?.css ?? ''].join('\n');
  }, [pageBlock, allBlocks, allLayouts]);

  const variables = useMemo(() => {
    if (!pageBlock) return undefined;

    const { layoutName, layoutKey, blockPairs } = parsePartialsFromHtml(pageBlock.html || '');

    const pageVars = pageBlock.variables as any;
    const pageContextOverrides = pageVars?.Context ?? {};

    const layout =
      allLayouts.find((item) => item.name === layoutName) ?? allBlocks.find((item) => item.name === layoutName);

    // build per-block context entries keyed by instance key and by name as fallback
    const blockContextEntries: Record<string, Record<string, any>> = {};
    const mergedProject: Record<string, any> = {};
    blockPairs.forEach(({ name, key }) => {
      const block = allBlocks.find((item) => item.name === name);

      const blockDefaults = (block?.variables as any)?.Context ?? {};

      Object.entries((block?.variables as any)?.Project ?? {}).forEach(([projectKey, value]) => {
        if (mergedProject[projectKey] === undefined) mergedProject[projectKey] = value;
      });

      const merged = { ...blockDefaults, ...pageContextOverrides };

      blockContextEntries[key] = merged;
      if (key !== name) blockContextEntries[name] = merged;
    });

    // flat merge of all block contexts — for cothrom-style layouts where {{> block}} uses root context
    const flatContext: Record<string, any> = {};
    const blockHtmlsForSections: { html: string }[] = [];

    blockPairs.forEach(({ name, key }) => {
      Object.entries(blockContextEntries[key] ?? {}).forEach(([varKey, value]) => {
        if (flatContext[varKey] === undefined) flatContext[varKey] = value;
      });
      const block = allBlocks.find((candidate) => candidate.name === name);
      if (block?.html) blockHtmlsForSections.push({ html: block.html });
    });

    // when {{#> layout [layoutKey]}} is used, @partial-block inside the layout runs with the
    // layout's scoped context (T[layoutKey]). Block partials with [blockKey] then resolve against
    // that scoped context, so T[layoutKey] must also contain all block keys as sub-entries.
    const layoutContext: Record<string, any> = {
      ...((layout?.variables as any)?.Context ?? {}),
      ...flatContext,
      ...blockContextEntries,
    };

    const contextByKey: Record<string, any> = {
      ...flatContext,
      ...blockContextEntries,
      ...(layoutKey ? { [layoutKey]: layoutContext } : {}),
    };

    return JSON.stringify({
      Context: contextByKey,
      Project: { ...mergedProject, ...(pageVars?.Project ?? {}) },
      System: {
        BasePath: '/',
        Pages: allPages.map((page) => ({ DisplayName: page.displayName ?? page.name, name: page.name })),
        Page: { DisplayName: pageBlock.displayName ?? 'Preview', name: pageBlock.name ?? 'preview' },
        Sections: extractSectionsFromBlockHtml(blockHtmlsForSections),
        CurrentYear: new Date().getFullYear(),
      },
    });
  }, [pageBlock, allBlocks, allLayouts, allPages]);

  return (
    <StyledCardPreview ref={containerRef}>
      {isPageLoading ? (
        <SkeletonLoader single count={1} height="100%" borderRadius={0} />
      ) : !pageBlock ? (
        <PageDocumentIcon />
      ) : (
        <StyledThumbnailInner $scale={scale}>
          <BlockPreview
            blocks={allBlocksForPartials}
            htmlCode={pageBlock.html || ''}
            cssCode={cssCode}
            jsCode=""
            variables={variables}
            hideHeader
            disableScripts
          />
        </StyledThumbnailInner>
      )}
    </StyledCardPreview>
  );
};

export const AddPageDialog: FC<AddPageDialogProps> = ({ open, templatePages, onSelect, onClose }) => {
  const { data: allBlocks = [], isLoading: isBlocksLoading } = useGetBlocksQuery();
  const { data: allLayouts = [], isLoading: isLayoutsLoading } = useGetLayoutsQuery();

  const handleSelect = (page: TemplatePage) => {
    onSelect(page);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        Available Pages
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>
      <DialogContent>
        {isBlocksLoading || isLayoutsLoading ? (
          <SkeletonLoader single count={3} height="200px" borderRadius={8} />
        ) : (
          <StyledGrid>
            {templatePages.map((page) => (
              <StyledPageCard key={page.name} onClick={() => handleSelect(page)}>
                {page.id ? (
                  <PageThumbnail
                    pageId={page.id}
                    allPages={templatePages}
                    allBlocks={allBlocks}
                    allLayouts={allLayouts}
                  />
                ) : (
                  <StyledCardPreview>
                    <PageDocumentIcon />
                  </StyledCardPreview>
                )}
                <StyledCardLabel>{page.displayName || page.name}</StyledCardLabel>
              </StyledPageCard>
            ))}
          </StyledGrid>
        )}
      </DialogContent>
    </Dialog>
  );
};
